import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { IPermitTypeField } from '~/client/graph'
import DrillDownFilter from '~/client/src/shared/components/DrillDownFilter'
import {
  FilterButton,
  GroupButton,
  SortButton,
} from '~/client/src/shared/components/Filters/triggers'
import {
  GroupByPopover,
  SortByPopover,
} from '~/client/src/shared/components/OptionsList'
import { useWorkflowFilterCategories } from '~/client/src/shared/components/WorkflowDirectory/components/WorkflowFilters/useWorkflowFilterCategories'
import {
  getWorkflowGroupingOptions,
  getWorkflowSortingOptions,
} from '~/client/src/shared/components/WorkflowDirectory/WorkflowDirectory.constants'
import {
  WorkflowGroupingKey,
  WorkflowSortingKey,
} from '~/client/src/shared/components/WorkflowList'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import InitialState from '~/client/src/shared/stores/InitialState'

import LinkedWorkflowDefaultsStore, {
  PopupType,
} from './LinkedWorkflowDefaults.store'

import './SetDefaultsSection.scss'

interface IProps {
  field: IPermitTypeField
  onChange: (updatedField: IPermitTypeField) => void
  tagsStore?: TagsStore
  workflowTypesStore?: WorkflowTypesStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
  workflowCategoriesStore?: WorkflowCategoriesStore
  projectsStore?: ProjectsStore
  state?: InitialState
}

const SetDefaultsSection: React.FC<IProps> = ({
  field,
  onChange,
  tagsStore,
  workflowTypesStore,
  companiesStore,
  projectMembersStore,
  locationAttributesStore,
  workflowCategoriesStore,
  projectsStore,
  state,
}) => {
  const [store] = React.useState(() => new LinkedWorkflowDefaultsStore())

  const sortOptions = React.useMemo(() => getWorkflowSortingOptions(), [])
  const groupByOptions = React.useMemo(() => getWorkflowGroupingOptions(), [])

  // Only show current project in the filter (like WorkflowDirectory does)
  const availableProjectIds = React.useMemo(() => {
    const activeProjectId = state?.activeProject?.id
    return activeProjectId ? [activeProjectId] : []
  }, [state?.activeProject?.id])

  // Use shared hook for filter categories with proper styling
  const categories = useWorkflowFilterCategories({
    tagsStore,
    permitTypesStore: workflowTypesStore,
    companiesStore,
    projectMembersStore,
    locationAttributesStore,
    formCategoriesStore: workflowCategoriesStore,
    projectsStore,
    availableProjectIds,
  })

  React.useEffect(() => {
    if (!tagsStore || !projectsStore || !workflowTypesStore) return
    store.init(
      field.defaultOptions,
      newOptions => {
        onChange({ ...field, defaultOptions: newOptions })
      },
      tagsStore,
      projectsStore,
      workflowTypesStore,
    )
  }, [
    store,
    field.id,
    field.defaultOptions,
    onChange,
    tagsStore,
    projectsStore,
    workflowTypesStore,
  ])

  const handleButtonClick = React.useCallback(
    (popup: PopupType) => {
      if (store.activePopup === popup) {
        store.closePopup()
      } else {
        store.openPopup(popup)
      }
    },
    [store],
  )

  const handleSortKeyChange = React.useCallback(
    (key: string | null) => {
      store.setSortDefaults(
        (key as WorkflowSortingKey) ?? null,
        store.sortOrder,
      )
    },
    [store],
  )

  const handleSortOrderChange = React.useCallback(
    (order: SortOrder) => {
      store.setSortDefaults(store.sortKey, order)
    },
    [store],
  )

  const handleGroupByChange = React.useCallback(
    (key: string | null) => {
      store.setGroupByDefault(key as WorkflowGroupingKey | null)
    },
    [store],
  )

  const handleFilterApply = React.useCallback(
    (selectedIds: string[]) => {
      store.setFilterDefaults(selectedIds)
    },
    [store],
  )

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) store.closePopup()
    },
    [store],
  )

  const sortTriggerLabel = React.useMemo(() => {
    const base = Localization.translator.sort
    const selected = store.sortKey
      ? sortOptions.find(o => o.key === store.sortKey)?.label
      : null
    if (!selected) return base
    const orderText =
      store.sortOrder === SortOrder.ASC
        ? Localization.translator.ascending
        : Localization.translator.descending
    return `${base}: ${selected} - ${orderText}`
  }, [store.sortKey, store.sortOrder, sortOptions])

  const groupByTriggerLabel = React.useMemo(() => {
    const base = Localization.translator.groupBy
    const selected = store.groupByKey
      ? groupByOptions.find(o => o.key === store.groupByKey)?.label
      : null
    return selected ? `${base}: ${selected}` : base
  }, [store.groupByKey, groupByOptions])

  const filterCount = store.filterTagIds.length

  return (
    <div className="set-defaults-section">
      <div className="text large medium-bold grey ml20 pt20">
        {Localization.translator.setDefaults}:
      </div>
      <div className="set-defaults-buttons ml20 py10">
        <SortByPopover
          open={store.activePopup === 'sort'}
          onOpenChange={handleOpenChange}
          side="right"
          options={sortOptions}
          selectedKey={store.sortKey}
          sortOrder={store.sortOrder}
          onChange={handleSortKeyChange}
          onSortOrderChange={handleSortOrderChange}
        >
          <SortButton
            label={sortTriggerLabel}
            onClick={() => handleButtonClick('sort')}
          />
        </SortByPopover>
        <GroupByPopover
          open={store.activePopup === 'groupBy'}
          onOpenChange={handleOpenChange}
          side="right"
          options={groupByOptions}
          selectedKey={store.groupByKey}
          onChange={handleGroupByChange}
          allowNone
        >
          <GroupButton
            label={groupByTriggerLabel}
            onClick={() => handleButtonClick('groupBy')}
          />
        </GroupByPopover>
        <DrillDownFilter.Popover
          open={store.activePopup === 'filters'}
          onOpenChange={handleOpenChange}
          side="right"
          trigger={
            <FilterButton
              onClick={() => handleButtonClick('filters')}
              label={Localization.translator.filters}
              badgeCount={filterCount > 0 ? filterCount : undefined}
            />
          }
        >
          <DrillDownFilter.Desktop
            title={Localization.translator.filterBy}
            categories={categories}
            initialSelection={store.filterTagIds}
            onApply={handleFilterApply}
          />
        </DrillDownFilter.Popover>
      </div>
    </div>
  )
}

export default inject(
  'tagsStore',
  'workflowTypesStore',
  'companiesStore',
  'projectMembersStore',
  'locationAttributesStore',
  'workflowCategoriesStore',
  'projectsStore',
  'state',
)(observer(SetDefaultsSection))
